import { useEffect, useState } from "react";
import { reservationsApi } from "@/lib/api";
import { toast } from "sonner";
import {
  PageHeader,
  DataTable,
  TH,
  TD,
  Badge,
  Kpi,
  Btn,
  EmptyState,
} from "@/components/DashboardUI";
import { ClipboardList, Check, X } from "lucide-react";

export default function Reservations() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    payees: 0,
    revenuTotal: 0,
    tauxConversion: 0,
  });

  const reload = async () => {
    try {
      const [list, s] = await Promise.all([
        reservationsApi.list(),
        reservationsApi.stats(),
      ]);
      setItems(list);
      setStats(s);
    } catch {
      toast.error("Erreur");
    }
  };

  useEffect(() => {
    reload();
  }, []);

  async function activer(id) {
    try {
      await reservationsApi.activer(id);
      toast.success("Jeu activé");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  }

  async function annuler(id) {
    if (!window.confirm("Annuler cette réservation ?")) return;
    try {
      await reservationsApi.annuler(id);
      toast.success("Annulée");
      reload();
    } catch {
      toast.error("Erreur");
    }
  }

  const statutTone = (s) =>
    s === "PAYE" ? "success" : s === "ANNULE" ? "danger" : "neutral";

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="reservations-page">
      <PageHeader
        chapter="Chapitre pro · Le carnet"
        title="Réservations"
        subtitle="Toutes les réservations, en attente ou honorées."
        testId="resa-header"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          value={stats.total}
          label="Total"
          accent="parchemin"
          testId="stat-total"
        />
        <Kpi
          value={stats.payees}
          label="Payées"
          accent="vert"
          testId="stat-payees"
        />
        <Kpi
          value={`${stats.tauxConversion}%`}
          label="Conversion"
          accent="or"
          testId="stat-conv"
        />
        <Kpi
          value={`${stats.revenuTotal.toFixed(2)}€`}
          label="Revenu"
          accent="or"
          testId="stat-revenu"
        />
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="w-6 h-6" />}
          title="Aucune réservation pour le moment."
          testId="resa-empty"
        />
      ) : (
        <DataTable testId="reservations-table">
          <thead>
            <tr>
              <TH>Date jeu</TH>
              <TH>Client</TH>
              <TH>Parcours</TH>
              <TH align="right">Joueurs</TH>
              <TH>Statut</TH>
              <TH align="right">Montant</TH>
              <TH align="right">Actions</TH>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr
                key={r.id}
                className="border-t border-or/10 hover:bg-noir-100/40 transition"
                data-testid={`resa-row-${r.id}`}
              >
                <TD className="font-mono text-parchemin/70 text-xs">
                  {r.dateJeu
                    ? new Date(r.dateJeu).toLocaleDateString("fr-FR")
                    : "—"}
                </TD>
                <TD>
                  <div className="font-medium text-parchemin">{r.prenom}</div>
                  <div className="text-xs font-mono text-parchemin/45 mt-0.5">
                    {r.email}
                  </div>
                </TD>
                <TD className="text-parchemin/70">
                  {r.parcours?.nom || "—"}
                </TD>
                <TD align="right">
                  <span className="font-mono">{r.nbJoueurs}</span>
                </TD>
                <TD>
                  <Badge tone={statutTone(r.statut)}>{r.statut}</Badge>
                </TD>
                <TD align="right" className="font-medium font-mono text-or">
                  {Number(r.montantTTC).toFixed(2)}€
                </TD>
                <TD align="right">
                  <div className="flex justify-end gap-2">
                    {r.statut === "PAYE" && !r.jeuActiveAt && (
                      <Btn
                        variant="secondary"
                        size="sm"
                        onClick={() => activer(r.id)}
                        data-testid={`activer-${r.id}`}
                      >
                        <Check className="w-3 h-3" />
                        Activer
                      </Btn>
                    )}
                    {r.statut !== "ANNULE" && (
                      <Btn
                        variant="dangerText"
                        size="sm"
                        onClick={() => annuler(r.id)}
                        data-testid={`annuler-${r.id}`}
                      >
                        <X className="w-3 h-3" />
                        Annuler
                      </Btn>
                    )}
                  </div>
                </TD>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </div>
  );
}
